import { useEffect, useMemo, useState, Fragment } from "react";
import React from "react";
import { toast } from "sonner";
import { Download, Filter, RefreshCw, ShieldCheck, Mail, X, Users, List, ChevronDown, ChevronRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../../api";
import { PageHeader, Btn, Badge } from "../../components/common";
import { overlayCloseHandlers } from "../../lib/overlayClose";
import { useAutoRefresh } from "../../lib/autoRefresh";

const ORIGENS = [
  { value: "", label: "Todas" },
  { value: "formulario", label: "Formulario" },
  { value: "chat-ai", label: "Chat AI" },
];

const TIPOS = [
  { value: "", label: "Politica + Marketing" },
  { value: "politica", label: "Politica aceite" },
  { value: "marketing", label: "Marketing opt-in" },
];

export default function ConsentHistory() {
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");
  const [origem, setOrigem] = useState("");
  const [tipo, setTipo] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [groupByEmail, setGroupByEmail] = useState(false);
  const [expanded, setExpanded] = useState({}); // email -> true

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (ate) params.ate = ate;
      if (origem) params.origem = origem;
      if (tipo) params.tipo = tipo;
      if (q) params.q = q;
      if (groupByEmail) params.group_by = "email";
      const { data } = await api.get("/crm/consents", { params });
      setItems(data.items || []);
      setGrouped(data.grouped || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [desde, ate, origem, tipo, groupByEmail]);
  useAutoRefresh(load); // eslint-disable-line

  const exportCsv = async () => {
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (ate) params.ate = ate;
      if (origem) params.origem = origem;
      if (tipo) params.tipo = tipo;
      const { data } = await api.get("/crm/consents/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `historico-consentimentos-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exportado.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const clearFilters = () => { setDesde(""); setAte(""); setOrigem(""); setTipo(""); setQ(""); };

  const summary = useMemo(() => {
    const politicaN = items.filter((i) => i.politica_aceite).length;
    const marketingN = items.filter((i) => i.marketing_consent).length;
    const uniqueContacts = new Set(items.map((i) => i.contact_id)).size;
    return { politicaN, marketingN, uniqueContacts };
  }, [items]);

  return (
    <div className="p-6" data-testid="consent-history-page">
      <PageHeader title="Historico de Consentimentos" subtitle="Evidencia RGPD de todas as aceitacoes de politica e marketing" icon={ShieldCheck} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-lg border border-tint p-3">
          <div className="text-[11px] uppercase tracking-wider text-muted2">Total evidencias</div>
          <div className="text-2xl font-bold text-navy" data-testid="stat-total">{total}</div>
        </div>
        <div className="bg-white rounded-lg border border-tint p-3">
          <div className="text-[11px] uppercase tracking-wider text-muted2">Contactos unicos</div>
          <div className="text-2xl font-bold text-navy">{summary.uniqueContacts}</div>
        </div>
        <div className="bg-white rounded-lg border border-tint p-3">
          <div className="text-[11px] uppercase tracking-wider text-muted2">Politica aceite</div>
          <div className="text-2xl font-bold text-green-600">{summary.politicaN}</div>
        </div>
        <div className="bg-white rounded-lg border border-tint p-3">
          <div className="text-[11px] uppercase tracking-wider text-muted2">Marketing opt-in</div>
          <div className="text-2xl font-bold text-blue">{summary.marketingN}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1.5">
          <Filter size={13} className="text-blue" />
          <span className="text-xs text-muted2">Desde:</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border border-tint rounded px-2 py-1 text-xs" data-testid="filter-desde" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted2">Ate:</span>
          <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} className="border border-tint rounded px-2 py-1 text-xs" data-testid="filter-ate" />
        </div>
        <select value={origem} onChange={(e) => setOrigem(e.target.value)} className="border border-tint rounded px-2 py-1 text-xs" data-testid="filter-origem">
          {ORIGENS.map((o) => <option key={o.value} value={o.value}>Origem: {o.label}</option>)}
        </select>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="border border-tint rounded px-2 py-1 text-xs" data-testid="filter-tipo">
          {TIPOS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="flex items-center gap-1.5">
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Nome ou email" className="border border-tint rounded px-2 py-1 text-xs w-48" data-testid="filter-q" />
          <Btn variant="ghost" onClick={load} testid="filter-search"><Filter size={12} /> Aplicar</Btn>
        </div>
        {(desde || ate || origem || tipo || q) && (
          <button onClick={clearFilters} className="text-xs text-muted2 hover:text-navy underline" data-testid="filter-clear">Limpar</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setGroupByEmail(!groupByEmail)} data-testid="toggle-group-by-email"
            className={`text-xs inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 ${groupByEmail ? "bg-blue text-white border-blue" : "bg-white border-tint text-navy hover:bg-slate-50"}`}>
            {groupByEmail ? <Users size={12} /> : <List size={12} />}
            {groupByEmail ? "Agrupado por titular" : "Vista plana"}
          </button>
          <Btn variant="ghost" onClick={load} testid="reload-btn"><RefreshCw size={12} /> Actualizar</Btn>
          <Btn onClick={exportCsv} testid="export-csv-btn"><Download size={13} /> Exportar CSV</Btn>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-tint overflow-hidden">
        {groupByEmail ? (
          <table className="w-full text-sm" data-testid="consent-grouped-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-muted2">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-3 py-2">Titular (email)</th>
                <th className="text-left px-3 py-2">Organizacao</th>
                <th className="text-right px-3 py-2">Evidencias</th>
                <th className="text-right px-3 py-2">Duplicados</th>
                <th className="text-right px-3 py-2">Politica</th>
                <th className="text-right px-3 py-2">Marketing</th>
                <th className="text-left px-3 py-2">Primeira</th>
                <th className="text-left px-3 py-2">Ultima</th>
                <th className="text-left px-3 py-2">Origens</th>
              </tr>
            </thead>
            <tbody data-testid="consent-grouped-tbody">
              {loading && (<tr><td colSpan={10} className="text-center py-6 text-muted2 text-xs">A carregar...</td></tr>)}
              {!loading && grouped.length === 0 && (
                <tr><td colSpan={10} className="text-center py-6 text-muted2 text-xs">Sem titulares para os filtros seleccionados.</td></tr>
              )}
              {grouped.map((g, idx) => {
                const key = g.email || `no-email-${idx}`;
                const isOpen = !!expanded[key];
                return (
                  <React.Fragment key={key}>
                    <tr className="border-t border-tint hover:bg-slate-50 cursor-pointer"
                      onClick={() => setExpanded({ ...expanded, [key]: !isOpen })}
                      data-testid={`consent-group-${idx}`}>
                      <td className="px-2 py-2 text-muted2">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-navy">{g.contact_nome || "-"}</div>
                        <div className="text-[11px] text-muted2">{g.email || "(sem email)"}</div>
                      </td>
                      <td className="px-3 py-2 text-navy">{g.account_nome || "-"}<div className="text-[11px] text-muted2">{g.account_nif || ""}</div></td>
                      <td className="px-3 py-2 text-right text-navy font-semibold" data-testid={`consent-group-total-${idx}`}>{g.total}</td>
                      <td className="px-3 py-2 text-right">
                        {g.duplicados > 0 ? (
                          <span className="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-300" data-testid={`consent-group-dup-${idx}`}>
                            {g.duplicados} duplic.
                          </span>
                        ) : <span className="text-muted2 text-xs">-</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-green-700">{g.politica_count}</td>
                      <td className="px-3 py-2 text-right text-blue">{g.marketing_count}</td>
                      <td className="px-3 py-2 text-[11px] text-muted2">{(g.primeira_em || "").slice(0, 10)}</td>
                      <td className="px-3 py-2 text-[11px] text-muted2">{(g.ultima_em || "").slice(0, 10)}</td>
                      <td className="px-3 py-2 text-[11px]"><div className="flex flex-wrap gap-1">{(g.origens || []).map((o) => <Badge key={o} value={o} />)}</div></td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/40" data-testid={`consent-group-detail-${idx}`}>
                        <td colSpan={10} className="px-4 py-2">
                          <div className="space-y-1.5">
                            {(g.evidencias || []).map((ev, k) => (
                              <div key={k} className="flex items-center justify-between text-xs bg-white border border-tint rounded px-2 py-1.5">
                                <span className="text-muted2">{(ev.em || "").slice(0, 19).replace("T", " ")}</span>
                                <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${ev.politica_aceite ? "bg-green-50 text-green-700 border-green-300" : "bg-slate-50 text-slate-500 border-slate-200"}`}>P: {ev.politica_aceite ? "sim" : "nao"}</span>
                                <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${ev.marketing_consent ? "bg-blue/10 text-blue border-blue/30" : "bg-slate-50 text-slate-500 border-slate-200"}`}>M: {ev.marketing_consent ? "opt-in" : "opt-out"}</span>
                                <span className="text-muted2 text-[11px]">Origem: {ev.origem || "-"}</span>
                                <button onClick={() => setDetail(ev)} className="text-blue text-[11px] hover:underline">Ver JSON</button>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-muted2">
            <tr>
              <th className="text-left px-3 py-2">Data</th>
              <th className="text-left px-3 py-2">Contacto</th>
              <th className="text-left px-3 py-2">Organizacao</th>
              <th className="text-left px-3 py-2">Origem</th>
              <th className="text-left px-3 py-2">Politica</th>
              <th className="text-left px-3 py-2">Marketing</th>
              <th className="text-left px-3 py-2">IP</th>
              <th className="text-right px-3 py-2">Evidencia</th>
            </tr>
          </thead>
          <tbody data-testid="consent-history-tbody">
            {loading && (
              <tr><td colSpan={8} className="text-center py-6 text-muted2 text-xs">A carregar...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-muted2 text-xs">Sem evidencias para os filtros seleccionados.</td></tr>
            )}
            {items.map((i, idx) => (
              <tr key={`${i.contact_id}-${i.em}-${idx}`} className="border-t border-tint hover:bg-slate-50" data-testid={`consent-row-${idx}`}>
                <td className="px-3 py-2 text-navy whitespace-nowrap">{(i.em || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2">
                  <div className="font-semibold text-navy">{i.contact_nome || "-"}</div>
                  <div className="text-[11px] text-muted2">{i.email || "-"}</div>
                </td>
                <td className="px-3 py-2 text-navy">{i.account_nome || "-"}<div className="text-[11px] text-muted2">{i.account_nif || ""}</div></td>
                <td className="px-3 py-2"><Badge value={i.origem || "-"} /></td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${i.politica_aceite ? "bg-green-50 text-green-700 border-green-300" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                    {i.politica_aceite ? "sim" : "nao"}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${i.marketing_consent ? "bg-blue/10 text-blue border-blue/30" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                    {i.marketing_consent ? "opt-in" : "opt-out"}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px] text-muted2 whitespace-nowrap">{i.ip || "-"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setDetail(i)} className="text-xs text-blue hover:underline" data-testid={`view-evidence-${idx}`}>Ver JSON</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" {...overlayCloseHandlers(() => setDetail(null))} data-testid="evidence-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto shadow-xl border border-tint">
            <div className="flex items-center justify-between px-4 py-3 border-b border-tint">
              <div className="flex items-center gap-2 text-navy font-semibold">
                <ShieldCheck size={16} /> Evidencia de consentimento (RGPD)
              </div>
              <button onClick={() => setDetail(null)} className="text-muted2 hover:text-navy" data-testid="evidence-close"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div><b>Contacto:</b> {detail.contact_nome} &lt;{detail.email}&gt;</div>
              <div><b>Organizacao:</b> {detail.account_nome} {detail.account_nif ? `(NIF ${detail.account_nif})` : ""}</div>
              <div><b>Data/Hora (UTC):</b> {detail.em}</div>
              <div><b>Origem:</b> {detail.origem} {detail.form_nome ? `- ${detail.form_nome}` : detail.session_id ? `- Sessao ${detail.session_id.slice(0, 8)}` : ""}</div>
              <div><b>IP:</b> {detail.ip || "-"}</div>
              <div><b>User-Agent:</b> <span className="text-[11px] break-all">{detail.user_agent || "-"}</span></div>
              <div><b>Versao Politica:</b> {detail.texto_politica_versao || "v1"}</div>
              <div className="flex gap-3 pt-2 border-t border-tint">
                <div className="flex items-center gap-1"><ShieldCheck size={14} className="text-green-600" /> Politica: <b>{detail.politica_aceite ? "aceite" : "nao aceite"}</b></div>
                <div className="flex items-center gap-1"><Mail size={14} className="text-blue" /> Marketing: <b>{detail.marketing_consent ? "opt-in" : "opt-out"}</b></div>
              </div>
              <details className="pt-2 border-t border-tint">
                <summary className="cursor-pointer text-xs text-muted2 hover:text-navy">Ver JSON completo</summary>
                <pre className="text-[10px] bg-slate-50 p-3 rounded mt-2 whitespace-pre-wrap overflow-auto">{JSON.stringify(detail, null, 2)}</pre>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
